/**
 * What landed on a job after it was already scheduled.
 *
 * The question this answers is not "what did the job cost" — the ladder does
 * that — but "what did we miss when we priced it". A crew turning up and
 * needing eight more sheets of decking is a cost, and also a piece of
 * information: the estimate was short. Nobody can act on that unless the two
 * are told apart, and once a job is closed out they are indistinguishable.
 *
 * The line is `jobs.scheduled_at`, stamped the first time the job goes on the
 * calendar and never moved afterwards. Anything created after that moment is
 * an addition. A job that was never scheduled has no additions — not zero
 * additions, but no answer, which is a different thing and is reported as such.
 *
 * Change orders are excluded, and that exclusion is the whole point. Agreed
 * extras also arrive late and also cost money, so counting them here would
 * bury the jobs that were genuinely under-priced under a pile of work the
 * customer asked for and paid for.
 */

import { sumBy, toCents, type Cents } from "./money.js";

export type AdditionKind = "material" | "labor" | "expense";

export interface AdditionRow {
  kind: AdditionKind;
  /** What it was, as written on the row. */
  description: string;
  amount: Cents;
  /** When it was added, ISO. */
  addedAt: string;
  /** Which document it landed on — "MO-900012", "WO-1000008", "Expense". */
  document: string;
}

export interface Additions {
  /** Null when the job has never been scheduled: unanswerable, not zero. */
  scheduledAt: string | null;
  rows: AdditionRow[];
  material: Cents;
  labor: Cents;
  expenses: Cents;
  total: Cents;
}

/**
 * A priced line with a date on it. What it is *called* is not here: a material
 * line has `name`, a work line has `description`, and neither table has the
 * other column — so each kind declares its own below and nothing reads a
 * spelling that is always absent.
 */
interface DatedLine {
  qty?: unknown;
  unit_price?: unknown;
  created_at?: string | null;
  /** Agreed extra work rather than something the estimate missed. */
  is_change_order?: boolean | null;
}

interface DatedMaterialLine extends DatedLine {
  name?: string | null;
}

interface DatedWorkLine extends DatedLine {
  description?: string | null;
}

export interface AdditionsInput {
  scheduledAt?: string | null;
  materialOrders?: readonly {
    order_number?: number | null;
    /** Material change orders are raised as a whole separate order. */
    is_change_order?: boolean | null;
    items?: readonly DatedMaterialLine[] | null;
  }[];
  workOrders?: readonly {
    order_number?: number | null;
    is_change_order?: boolean | null;
    items?: readonly DatedWorkLine[] | null;
  }[];
  expenses?: readonly {
    name?: string | null;
    amount?: unknown;
    created_at?: string | null;
    is_change_order?: boolean | null;
  }[];
}

const lineAmount = (line: DatedLine): Cents =>
  toCents(Number(line.qty ?? 0) * Number(line.unit_price ?? 0));

/** Rows created strictly after the moment the job was scheduled. */
export function computeAdditions(input: AdditionsInput): Additions {
  const scheduledAt = input.scheduledAt ?? null;
  const empty: Additions = {
    scheduledAt,
    rows: [],
    material: toCents(0),
    labor: toCents(0),
    expenses: toCents(0),
    total: toCents(0),
  };
  if (!scheduledAt) return empty;

  const after = Date.parse(scheduledAt);
  if (Number.isNaN(after)) return empty;

  const isLate = (when: string | null | undefined): boolean => {
    if (!when) return false;
    const t = Date.parse(when);
    return !Number.isNaN(t) && t > after;
  };

  const rows: AdditionRow[] = [];

  for (const order of input.materialOrders ?? []) {
    // Materials are ordered again for an extra, so the whole order is the
    // change order and every line on it is agreed work.
    if (order.is_change_order) continue;
    for (const item of order.items ?? []) {
      if (!isLate(item.created_at) || item.is_change_order) continue;
      rows.push({
        kind: "material",
        description: item.name ?? "(unnamed)",
        amount: lineAmount(item),
        addedAt: item.created_at!,
        document: order.order_number ? `MO-${order.order_number}` : "Material",
      });
    }
  }

  for (const order of input.workOrders ?? []) {
    if (order.is_change_order) continue;
    for (const item of order.items ?? []) {
      // A work order is one document with its extras inside it, so here the
      // flag is per line.
      if (!isLate(item.created_at) || item.is_change_order) continue;
      rows.push({
        kind: "labor",
        description: item.description ?? "(unnamed)",
        amount: lineAmount(item),
        addedAt: item.created_at!,
        document: order.order_number ? `WO-${order.order_number}` : "Labor",
      });
    }
  }

  for (const expense of input.expenses ?? []) {
    if (!isLate(expense.created_at) || expense.is_change_order) continue;
    rows.push({
      kind: "expense",
      description: expense.name ?? "(unnamed)",
      amount: toCents(expense.amount),
      addedAt: expense.created_at!,
      document: "Expense",
    });
  }

  // Newest first: what went wrong most recently is what somebody is looking for.
  rows.sort((a, b) => Date.parse(b.addedAt) - Date.parse(a.addedAt));

  const of = (kind: AdditionKind) =>
    sumBy(
      rows.filter((r) => r.kind === kind),
      (r) => r.amount,
    );

  return {
    scheduledAt,
    rows,
    material: of("material"),
    labor: of("labor"),
    expenses: of("expense"),
    total: sumBy(rows, (r) => r.amount),
  };
}
